import React from 'react';
import "./RelatorioDispAdmin.css";

const DisponibilidadeChart = ({ response, month }) => {

    const monthsOptions = [
        { label: "Janeiro", month: '01'},
        { label: "Fevereiro", month: '02' },
        { label: "Março", month: '03'},
        { label: "Abril", month: '04'},
        { label: "Maio", month: '05'},
        { label: "Junho", month: '06'},
        { label: "Julho", month: '07'},
        { label: "Agosto", month: '08' },
        { label: "Setembro", month: '09' },
        { label: "Outubro", month: '10'},
        { label: "Novembro", month: '11'},
        { label: "Dezembro", month: '12'},
      ];

    const numeroMes = monthsOptions.find((item) => {
        const monthName = month?.split(" ")[0];
        return item.label === monthName;
      })?.month;

    const getIntervalIndex = (interval) => {
        if (!interval || !String(interval).includes('-')) return -1;
        const [start] = String(interval).split('-');
        const [horaTexto, minutoTexto] = start.split(':');
        const startHour = parseInt(horaTexto, 10);
        const startMinutes = parseInt(minutoTexto, 10);
        if (!Number.isFinite(startHour) || !Number.isFinite(startMinutes)) return -1;
        return (startHour * 2) + Math.floor(startMinutes / 30);
    };

    const createMatrixFromResponse = (filteredData) => {
        const rows = 48;
        const cols = 31;
        const matrix = Array.from({ length: rows }, () =>
            Array.from({ length: cols }, () => 0)
        );

        // DISPONIBILIDADE_CONTINUA_2026
        // A máquina envia pontos de comunicação. O relatório não pode mostrar uma meia-hora
        // preta isolada entre períodos azuis, pois isso dá a impressão falsa de offline.
        const indicesPorDia = Array.from({ length: cols }, () => []);

        (Array.isArray(filteredData) ? filteredData : []).forEach(item => {
            const [day] = String(item?.data || '').split('/');
            const dayIndex = parseInt(day, 10) - 1;
            const intervalIndex = getIntervalIndex(item?.intervalo);

            if (dayIndex >= 0 && dayIndex < cols && intervalIndex >= 0 && intervalIndex < rows) {
                indicesPorDia[dayIndex].push(intervalIndex);
            }
        });

        const marcar = (linha, coluna) => {
            if (linha >= 0 && linha < rows && coluna >= 0 && coluna < cols) {
                matrix[linha][coluna] = 1;
            }
        };

        indicesPorDia.forEach((indices, dayIndex) => {
            if (!indices.length) return;

            const ordenados = [...new Set(indices)].sort((a, b) => a - b);

            // Registra os pontos online originais.
            ordenados.forEach((intervalIndex) => marcar(intervalIndex, dayIndex));

            // REGRA_HORA_INTEIRA_AZUL_2026
            // Se qualquer metade da hora teve comunicação, as duas metades da mesma hora ficam azuis.
            for (let hora = 0; hora < 24; hora += 1) {
                const primeiraMetade = hora * 2;
                const segundaMetade = primeiraMetade + 1;
                if (matrix[primeiraMetade][dayIndex] === 1 || matrix[segundaMetade][dayIndex] === 1) {
                    marcar(primeiraMetade, dayIndex);
                    marcar(segundaMetade, dayIndex);
                }
            }

            // FECHA_BURACO_PRETO_ISOLADO_2026
            // Depois da normalização por hora, elimina qualquer única faixa preta de 30 minutos
            // que ainda tenha azul imediatamente acima e abaixo na mesma coluna/dia.
            const colunaAntes = Array.from({ length: rows }, (_, linha) => matrix[linha][dayIndex]);
            for (let linha = 1; linha < rows - 1; linha += 1) {
                if (
                    colunaAntes[linha] === 0 &&
                    colunaAntes[linha - 1] === 1 &&
                    colunaAntes[linha + 1] === 1
                ) {
                    marcar(linha, dayIndex);
                }
            }

            // CONTINUIDADE_ENTRE_LEITURAS_2026
            // Se duas leituras reais estiverem separadas por no máximo 1 hora, pinta também o
            // intervalo entre elas. Isso remove o padrão falso azul/preto/azul causado pelo
            // intervalo normal de comunicação da máquina.
            for (let i = 1; i < ordenados.length; i += 1) {
                const anterior = ordenados[i - 1];
                const atual = ordenados[i];
                const distancia = atual - anterior;

                if (distancia > 0 && distancia <= 2) {
                    for (let linha = anterior; linha <= atual; linha += 1) {
                        marcar(linha, dayIndex);
                    }
                }
            }

            // Faz uma última normalização por hora depois do preenchimento de continuidade.
            for (let hora = 0; hora < 24; hora += 1) {
                const primeiraMetade = hora * 2;
                const segundaMetade = primeiraMetade + 1;
                if (matrix[primeiraMetade][dayIndex] === 1 || matrix[segundaMetade][dayIndex] === 1) {
                    marcar(primeiraMetade, dayIndex);
                    marcar(segundaMetade, dayIndex);
                }
            }
        });

        return matrix;
    };

    const getTimeRange = (index) => {
        const startHour = Math.floor(index / 2);
        const startMinutes = (index % 2) * 30;
        const endHour = Math.floor((index + 1) / 2);
        const endMinutes = ((index + 1) % 2) * 30;

        const formatTime = (hour, minutes) => `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

        return `${formatTime(startHour, startMinutes)}-${formatTime(endHour, endMinutes)}`;
    };

    const matrix = createMatrixFromResponse(response);

    return (
        <div className="availability-chart-shell">
            {
                numeroMes ?
                <div className="availability-grid-wrap">
                    <h2 className="availability-grid-title">{`Disponibilidade em ${month}`}</h2>
                    <div className="availability-grid-scroll">
                        <table className="availability-table">
                            <thead>
                                <tr>
                                    <th className="table-header-cell">Tempo/Dia</th>
                                    {[...Array(31)].map((_, index) => (
                                        <th key={index} className="table-header-cell">
                                            {index > 8 ? `${index + 1}/${numeroMes}` : `0${index + 1}/${numeroMes}`}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.map((row, y) => (
                                    <tr key={y}>
                                        <td className="time-cell">{getTimeRange(y)}</td>
                                        {row.map((value, x) => (
                                            <td
                                                key={x}
                                                className={value === 1 ? 'availability-cell availability-cell-online' : 'availability-cell availability-cell-offline'}
                                                title={value === 1 ? 'Máquina online neste período' : 'Máquina offline neste período'}
                                            />
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="availability-legend">
                        <span><i className="availability-legend-online" /> Online</span>
                        <span><i className="availability-legend-offline" /> Offline</span>
                    </div>
                </div>
                :
                ''
            }
        </div>
    );
};

export default DisponibilidadeChart;
